/**
 * UTM (Urchin Tracking Module) parameter utilities
 * Handles persistence and management of UTM parameters for tracking
 * Supports any UTM parameter dynamically
 */

const UTM_STORAGE_KEY = "ev_utm_params";

export interface UtmParams {
  [key: string]: string | null | undefined;
}

export const ONE_TIME_CHECKOUT_FLOWS = {
  FLOW1: "flujo1",
  FLOW2: "flujo2",
  FLOW3: "flujo3",
  FLOW5: "flujo5",
} as const;

export type OneTimeCheckoutFlow =
  (typeof ONE_TIME_CHECKOUT_FLOWS)[keyof typeof ONE_TIME_CHECKOUT_FLOWS];

export interface OneTimeCheckoutAttribution {
  flow: OneTimeCheckoutFlow;
  campaignInternal: string;
  originPath?: string;
}

export interface OneTimePaymentUtmPayload {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
  utm_id?: string;
  ev_flow?: string;
  ev_campaign_internal?: string;
  ev_origin_path?: string;
}

const UTM_DEFAULTS = {
  source: "comprab2c",
  term: "CTASI",
  content: "Candidato",
} as const;

function normalizeUtmValue(value: string | null | undefined): string | undefined {
  if (!value || typeof value !== "string") {
    return undefined;
  }
  const trimmed = value.trim();
  return trimmed.length ? trimmed : undefined;
}

export function getCurrentPathname(): string | undefined {
  if (typeof window === "undefined") {
    return undefined;
  }

  return window.location.pathname;
}

export function buildOneTimePaymentUtmPayload(
  attribution: OneTimeCheckoutAttribution
): OneTimePaymentUtmPayload {
  const stored = getUtmFromStorage() ?? {};
  const utmId = normalizeUtmValue(stored.utm_id);
  const fallbackOriginPath = getCurrentPathname();
  const originPath = normalizeUtmValue(attribution.originPath) ?? fallbackOriginPath;

  return {
    utm_source: normalizeUtmValue(stored.utm_source) ?? UTM_DEFAULTS.source,
    utm_medium: normalizeUtmValue(stored.utm_medium) ?? attribution.flow,
    utm_campaign: normalizeUtmValue(stored.utm_campaign) ?? attribution.campaignInternal,
    utm_term: normalizeUtmValue(stored.utm_term) ?? UTM_DEFAULTS.term,
    utm_content: normalizeUtmValue(stored.utm_content) ?? UTM_DEFAULTS.content,
    ...(utmId && { utm_id: utmId }),
    ev_flow: attribution.flow,
    ev_campaign_internal: attribution.campaignInternal,
    ...(originPath && { ev_origin_path: originPath }),
  };
}

/**
 * Retrieves UTM parameters from sessionStorage
 * @returns UTM parameters object or null if not found/invalid
 */
export function getUtmFromStorage(): UtmParams | null {
  try {
    const stored = sessionStorage.getItem(UTM_STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch (error) {
    console.error("Error reading UTM params from sessionStorage:", error);
    return null;
  }
}

/**
 * Saves UTM parameters to sessionStorage
 * @param params - UTM parameters to save
 */
export function saveUtmToStorage(params: UtmParams): void {
  try {
    sessionStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(params));
  } catch (error) {
    console.error("Error saving UTM params to sessionStorage:", error);
  }
}

/**
 * Gets initial UTM parameters for SSR compatibility
 * @returns Empty object if running on server, stored params if on client
 */
export function getInitialUtmParams(): UtmParams {
  if (typeof window === "undefined") {
    return {};
  }

  try {
    const stored = sessionStorage.getItem(UTM_STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}

/**
 * Checks if UTM parameters exist in the provided object
 * @param params - UTM parameters to check
 * @returns true if any UTM parameter has a value
 */
export function hasUtmParams(params: UtmParams): boolean {
  return Object.values(params).some((value) => Boolean(value));
}

/**
 * Creates a UTM parameters object from a record of key-value pairs
 * @param params - Record of UTM parameters
 * @returns UTM parameters object
 */
export function createUtmParams(params: Record<string, string | null | undefined>): UtmParams {
  return { ...params };
}

/**
 * Extracts UTM parameters from URL search params
 * @param searchParams - URLSearchParams object
 * @returns UTM parameters object
 */
export function extractUtmFromUrl(searchParams: URLSearchParams): UtmParams {
  const utmParams: UtmParams = {};

  for (const [key, value] of searchParams.entries()) {
    if (key.startsWith("utm_")) {
      utmParams[key] = value;
    }
  }

  return utmParams;
}

/**
 * Converts UTM parameters to query string
 * @param params - UTM parameters object
 * @returns Query string with UTM parameters
 */
export function utmParamsToQueryString(params: UtmParams): string {
  return Object.entries(params)
    .filter(([, value]) => Boolean(value) && typeof value === "string")
    .map(([key, value]) => `${key}=${encodeURIComponent(value as string)}`)
    .join("&");
}

/**
 * Clears UTM parameters from sessionStorage
 */
export function clearUtmFromStorage(): void {
  try {
    sessionStorage.removeItem(UTM_STORAGE_KEY);
  } catch (error) {
    console.error("Error clearing UTM params from sessionStorage:", error);
  }
}
